import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin, message } from "antd";
import requests from "../../axios/requests";
import styles from "./CertificateTemplate.module.scss";

// Редактор шаблона сертификата учебного центра: фон + поля, которые перетаскиваются
// по фону. Координаты и размер хранятся в % от фона - так же их понимает рендер на
// бэкенде (services/certificate.py), а «Предпросмотр» рисует настоящий сертификат им же.

const FIELD_LABELS = {
  fio: "ФИО ученика",
  course: "Название курса",
  hours: "Академические часы",
  date: "Дата выдачи",
  number: "Номер сертификата",
  date_start: "Начало обучения",
  date_end: "Окончание обучения",
};
const FIELD_ORDER = ["fio", "course", "hours", "date", "number", "date_start", "date_end"];
const SAMPLE = {
  fio: ["Иванова Анна Сергеевна", "Иванова Анна Сергеевна"],
  course: ["«Название курса»", "Название курса"],
  hours: ["36 академических часов", "36"],
  date: ["Дата выдачи: 03.10.2026", "03.10.2026"],
  number: ["№ 000123", "000123"],
  date_start: ["с 01.09.2026", "01.09.2026"],
  date_end: ["по 30.09.2026", "30.09.2026"],
};
// Совпадает с DEFAULT_FIELDS в backend services/certificate.py
const DEFAULT_FIELDS = {
  fio: { enabled: true, x: 50, y: 45, size: 4.4, color: "#0F172A", font: "black", align: "center", label: true },
  course: { enabled: true, x: 50, y: 60, size: 3.0, color: "#0F172A", font: "semibold", align: "center", label: true },
  hours: { enabled: true, x: 50, y: 69, size: 2.2, color: "#64748B", font: "medium", align: "center", label: true },
  date: { enabled: true, x: 12, y: 88, size: 1.9, color: "#64748B", font: "medium", align: "left", label: true },
  number: { enabled: true, x: 88, y: 88, size: 1.9, color: "#64748B", font: "medium", align: "right", label: true },
  date_start: { enabled: false, x: 30, y: 78, size: 1.9, color: "#0F172A", font: "medium", align: "center", label: true },
  date_end: { enabled: false, x: 70, y: 78, size: 1.9, color: "#0F172A", font: "medium", align: "center", label: true },
};
const FONT_WEIGHT = { black: 800, semibold: 600, medium: 500 };
const TRANSLATE_X = { left: "0", center: "-50%", right: "-100%" };
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const CertificateTemplate = () => {
  const [loading, setLoading] = useState(true);
  const [background, setBackground] = useState(null);
  const [fields, setFields] = useState({});
  const [selected, setSelected] = useState("fio");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const fileRef = useRef(null);

  const applyResponse = useCallback((data) => {
    setBackground(data?.background || null);
    setFields(data?.fields || {});
    setDirty(false);
  }, []);

  useEffect(() => {
    requests.organization
      .get_certificate_template()
      .then((res) => {
        applyResponse(res.data);
      })
      .catch(() => message.error("Не удалось загрузить шаблон"))
      .finally(() => setLoading(false));
  }, [applyResponse]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setCanvasWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [background, loading]);

  const updateField = (key, patch) => {
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    setDirty(true);
  };

  const onPointerDown = (key) => (e) => {
    e.preventDefault();
    setSelected(key);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { key, rect, startX: e.clientX, startY: e.clientY, x: fields[key].x, y: fields[key].y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const x = clamp(d.x + ((e.clientX - d.startX) / d.rect.width) * 100, 0, 100);
    const y = clamp(d.y + ((e.clientY - d.startY) / d.rect.height) * 100, 0, 100);
    updateField(d.key, { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  const uploadBackground = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("background", file);
    setBusy(true);
    requests.organization
      .patch_certificate_template(form)
      .then((res) => {
        applyResponse(res.data);
        message.success("Фон загружен");
      })
      .catch((err) => message.error(err?.response?.data?.error || "Не удалось загрузить фон"))
      .finally(() => setBusy(false));
  };
  const save = () => {
    setBusy(true);
    requests.organization
      .patch_certificate_template({ fields })
      .then((res) => {
        applyResponse(res.data);
        message.success("Шаблон сохранён");
      })
      .catch((err) => message.error(err?.response?.data?.error || "Не удалось сохранить"))
      .finally(() => setBusy(false));
  };
  const showPreview = () => {
    setBusy(true);
    requests.organization
      .preview_certificate_template({ fields })
      .then((res) => setPreview(res.data?.image))
      .catch((err) => message.error(err?.response?.data?.error || "Не удалось построить предпросмотр"))
      .finally(() => setBusy(false));
  };
  const resetLayout = () => {
    setFields(DEFAULT_FIELDS);
    setDirty(true);
  };

  if (loading) return <Spin />;
  const current = fields[selected];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Шаблон сертификата</h1>
          <p className={styles.lead}>
            Один шаблон на учебный центр. Его получат ученики курсов, где отмечено «Курс с сертификатом». Загрузите фон и
            перетащите поля на нужные места.
          </p>
        </div>
        <div className={styles.actions}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={(e) => {
              uploadBackground(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button type="button" className={styles.btnGhost} onClick={() => fileRef.current?.click()} disabled={busy}>
            {background ? "Заменить фон" : "Загрузить фон"}
          </button>
          {background && (
            <>
              <button type="button" className={styles.btnGhost} onClick={showPreview} disabled={busy}>
                Предпросмотр
              </button>
              <button type="button" className={styles.btnPrimary} onClick={save} disabled={busy || !dirty}>
                {dirty ? "Сохранить" : "Сохранено"}
              </button>
            </>
          )}
        </div>
      </div>

      {!background ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Фон ещё не загружен</div>
          <div className={styles.muted}>
            JPG или PNG до 15 МБ. Лучше всего A4: 2480×1754 px (альбомный) или 1754×2480 px (книжный). Пока фона нет,
            ученики получают стандартный сертификат Skillometer.
          </div>
          <button type="button" className={styles.btnPrimary} onClick={() => fileRef.current?.click()} disabled={busy}>
            Загрузить фон
          </button>
        </div>
      ) : (
        <div className={styles.workspace}>
          <div className={styles.canvasWrap}>
            <div
              ref={canvasRef}
              className={styles.canvas}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              <img src={background} alt="Фон сертификата" className={styles.bg} draggable={false} />
              {FIELD_ORDER.filter((k) => fields[k]?.enabled).map((key) => {
                const f = fields[key];
                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    aria-label={FIELD_LABELS[key]}
                    className={`${styles.field} ${selected === key ? styles.fieldActive : ""}`}
                    style={{
                      left: `${f.x}%`,
                      top: `${f.y}%`,
                      transform: `translate(${TRANSLATE_X[f.align] || "-50%"}, -50%)`,
                      fontSize: `${(f.size / 100) * canvasWidth}px`,
                      color: f.color,
                      fontWeight: FONT_WEIGHT[f.font] || 500,
                      textAlign: f.align,
                    }}
                    onPointerDown={onPointerDown(key)}
                    onFocus={() => setSelected(key)}
                  >
                    {SAMPLE[key][f.label ? 0 : 1]}
                  </div>
                );
              })}
            </div>
            <div className={styles.hint}>
              Перетаскивайте поля мышью. Переносы длинных строк точно видны в «Предпросмотре» - он рисует настоящий
              сертификат.
            </div>
          </div>

          <aside className={styles.panel}>
            <div className={styles.panelTitle}>Поля</div>
            <div className={styles.fieldList}>
              {FIELD_ORDER.map((key) => (
                <label key={key} className={`${styles.fieldRow} ${selected === key ? styles.fieldRowActive : ""}`}>
                  <input
                    type="checkbox"
                    checked={!!fields[key]?.enabled}
                    onChange={(e) => {
                      updateField(key, { enabled: e.target.checked });
                      setSelected(key);
                    }}
                  />
                  <span onClick={() => setSelected(key)}>{FIELD_LABELS[key]}</span>
                </label>
              ))}
            </div>

            {current && (
              <div className={styles.props}>
                <div className={styles.panelTitle}>{FIELD_LABELS[selected]}</div>
                <label className={styles.prop}>
                  Размер текста
                  <input
                    type="range"
                    min="0.5"
                    max="12"
                    step="0.1"
                    value={current.size}
                    onChange={(e) => updateField(selected, { size: Number(e.target.value) })}
                  />
                </label>
                <label className={styles.prop}>
                  Цвет
                  <input type="color" value={current.color} onChange={(e) => updateField(selected, { color: e.target.value })} />
                </label>
                <label className={styles.prop}>
                  Шрифт
                  <select value={current.font} onChange={(e) => updateField(selected, { font: e.target.value })}>
                    <option value="black">Жирный</option>
                    <option value="semibold">Полужирный</option>
                    <option value="medium">Обычный</option>
                  </select>
                </label>
                <label className={styles.prop}>
                  Выравнивание
                  <select value={current.align} onChange={(e) => updateField(selected, { align: e.target.value })}>
                    <option value="left">По левому краю</option>
                    <option value="center">По центру</option>
                    <option value="right">По правому краю</option>
                  </select>
                </label>
                {selected !== "fio" && (
                  <label className={styles.propCheck}>
                    <input
                      type="checkbox"
                      checked={!current.label}
                      onChange={(e) => updateField(selected, { label: !e.target.checked })}
                    />
                    Только значение (для бланков вида «№ ___», «в объёме ___ часов»)
                  </label>
                )}
                <div className={styles.coords}>
                  X {current.x}% · Y {current.y}%
                </div>
              </div>
            )}

            <button type="button" className={styles.linkBtn} onClick={resetLayout} disabled={busy}>
              Сбросить раскладку
            </button>
          </aside>
        </div>
      )}

      <Modal visible={!!preview} open={!!preview} onCancel={() => setPreview(null)} footer={null} width={1000} centered>
        {preview && <img src={preview} alt="Предпросмотр сертификата" style={{ width: "100%", display: "block" }} />}
      </Modal>
    </div>
  );
};

export default CertificateTemplate;
